import { useWeeklySummary } from '@/hooks/useWeeklySummary';
import { Calendar, Clock, Sparkles, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Activity } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';

interface WeeklySummaryCardProps {
  variant?: 'compact' | 'full';
  className?: string;
}

const WeeklySummaryCard = ({ variant = 'compact', className = '' }: WeeklySummaryCardProps) => {
  const { getWeeklyStats, getPreviousWeekComparison, refreshInsight } = useWeeklySummary();
  const [expanded, setExpanded] = useState(false);
  const { t } = useLanguage();
  
  const stats = getWeeklyStats;
  const comparison = getPreviousWeekComparison;

  const TrendIcon = ({ diff }: { diff: number }) => {
    if (diff > 0) return <TrendingUp className="w-3 h-3 text-emerald-400/60" />;
    if (diff < 0) return <TrendingDown className="w-3 h-3 text-rose-400/60" />;
    return <Minus className="w-3 h-3 text-muted-foreground/30" />;
  };

  if (variant === 'compact') {
    return (
      <button
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-2.5 text-sm text-left group ${className}`}
      >
        <div className="p-1.5 bg-foreground/5 rounded-sm group-hover:bg-foreground/10 transition-colors">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground/50" />
        </div>
        <span className="text-muted-foreground/60 group-hover:text-muted-foreground transition-colors">
          {t('week') || 'Week'} {stats.weekNumber}
        </span>
        <span className="text-foreground/60 font-light">
          {stats.roomsThisWeek} {t('rooms') || 'rooms'}
        </span>
        {comparison && (
          <TrendIcon diff={comparison.roomsDiff} />
        )}
      </button>
    );
  }

  return (
    <div className={`border border-border/10 bg-card/20 overflow-hidden ${className}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 flex items-center justify-between text-left hover:bg-card/40 transition-all duration-200 group"
      >
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-foreground/5 group-hover:bg-foreground/10 transition-colors">
            <Calendar className="w-5 h-5 text-muted-foreground/40 group-hover:text-muted-foreground/60 transition-colors" />
          </div>
          <div>
            <div className="text-sm font-light text-foreground/90">{t('week') || 'Week'} {stats.weekNumber} {t('weekly_summary') || 'Summary'}</div>
            <div className="text-xs text-muted-foreground/40 mt-0.5">
              {stats.roomsThisWeek} {t('rooms') || 'rooms'} · {stats.fragmentsThisWeek} {t('fragments') || 'fragments'}
            </div>
          </div>
        </div>
        <div className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground/50" />
        </div>
      </button>

      <div className={`overflow-hidden transition-all duration-300 ${expanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 pt-0 border-t border-border/5">
          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="p-4 bg-gradient-to-br from-card/60 to-card/30 border border-border/5">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-3.5 h-3.5 text-blue-400/50" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/40">{t('rooms') || 'Rooms'}</span>
              </div>
              <div className="text-xl font-extralight text-foreground/90">{stats.roomsThisWeek}</div>
              {comparison && (
                <div className="flex items-center gap-1.5 mt-2">
                  <TrendIcon diff={comparison.roomsDiff} />
                  <span className={`text-xs ${comparison.roomsDiff > 0 ? 'text-emerald-400/60' : comparison.roomsDiff < 0 ? 'text-rose-400/60' : 'text-muted-foreground/30'}`}>
                    {comparison.roomsDiff > 0 ? '+' : ''}{comparison.roomsDiff}
                  </span>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-gradient-to-br from-card/60 to-card/30 border border-border/5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-400/50" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/40">{t('fragments') || 'Fragments'}</span>
              </div>
              <div className="text-xl font-extralight text-foreground/90">{stats.fragmentsThisWeek}</div>
              {comparison && (
                <div className="flex items-center gap-1.5 mt-2">
                  <TrendIcon diff={comparison.fragmentsDiff} />
                  <span className={`text-xs ${comparison.fragmentsDiff > 0 ? 'text-emerald-400/60' : comparison.fragmentsDiff < 0 ? 'text-rose-400/60' : 'text-muted-foreground/30'}`}>
                    {comparison.fragmentsDiff > 0 ? '+' : ''}{comparison.fragmentsDiff}
                  </span>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-gradient-to-br from-card/60 to-card/30 border border-border/5">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-3.5 h-3.5 text-purple-400/50" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/40">{t('days') || 'Days'}</span>
              </div>
              <div className="text-xl font-extralight text-foreground/90">{stats.streakDaysThisWeek}</div>
              {comparison && (
                <div className="flex items-center gap-1.5 mt-2">
                  <TrendIcon diff={comparison.streakDiff} />
                  <span className={`text-xs ${comparison.streakDiff > 0 ? 'text-emerald-400/60' : comparison.streakDiff < 0 ? 'text-rose-400/60' : 'text-muted-foreground/30'}`}>
                    {comparison.streakDiff > 0 ? '+' : ''}{comparison.streakDiff}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Insight */}
          {stats.insight && (
            <button 
              className="w-full p-4 border border-border/5 bg-gradient-to-r from-foreground/[0.02] to-transparent text-left group/insight hover:from-foreground/[0.04] transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                refreshInsight();
              }}
            >
              <div className="flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-foreground/20 mt-0.5 group-hover/insight:text-foreground/40 transition-colors" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground/60 italic leading-relaxed">
                    "{stats.insight}"
                  </p>
                  <p className="text-[10px] text-muted-foreground/25 mt-2 group-hover/insight:text-muted-foreground/40 transition-colors">
                    {t('refresh_insight') || 'tap for another insight'}
                  </p>
                </div>
              </div>
            </button>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between text-[10px] text-muted-foreground/25 mt-4 pt-4 border-t border-border/5">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {stats.minutesThisWeek} min total
            </span>
            <span>Resets Monday</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklySummaryCard;
